import { useCallback, useEffect, useMemo } from 'react';
import { useLocale } from 'next-intl';
import { useLazyQuery } from '@apollo/client';

export const usePostsSlider = ({
  placement,
  query,
  isBlog = false,
  isReview = false,
}) => {
  const locale = useLocale();

  const variables = useMemo(
    () =>
      isBlog || isReview
        ? { slug: placement, locale }
        : { filter: { placement, locale } },
    [locale, isBlog, isReview, placement]
  );

  const [fetchPosts, { data, loading, called, fetchMore }] = useLazyQuery(
    query,
    {
      variables: { ...variables, page: 1 },
      notifyOnNetworkStatusChange: true,
    }
  );

  const blogs = useMemo(
    () =>
      (isReview
        ? data?.guestReviews && [{ posts: data?.guestReviews }]
        : isBlog
        ? data?.showBlog && [data?.showBlog]
        : data?.blogs?.collection) || [],
    [isBlog, isReview, data]
  );

  const posts = useMemo(
    () =>
      blogs?.length
        ? blogs[0]?.posts.collection.map(({ slug, ...params }) => ({
            ...params,
            slug: `/blogs/${blogs[0]?.slug}/${slug}`,
          }))
        : [],
    [blogs]
  );

  const metadata = useMemo(
    () => ({
      title: blogs[0]?.name,
      totalPages: blogs[0]?.posts?.metadata?.totalPages,
      currentPage: blogs[0]?.posts?.metadata?.currentPage,
      hasNextPage:
        blogs[0]?.posts?.metadata?.currentPage <
        blogs[0]?.posts?.metadata?.totalPages,
    }),
    [blogs]
  );

  useEffect(() => {
    fetchPosts({
      variables: { ...variables, page: 1 },
    });
  }, [fetchPosts, variables]);

  const handleNext = useCallback(() => {
    if (metadata.hasNextPage) {
      const nextPage = metadata.currentPage + 1;

      return fetchMore({
        variables: { ...variables, page: nextPage },
        updateQuery(previousData, { fetchMoreResult }) {
          return {
            blogs: {
              collection: [
                {
                  ...previousData.blogs.collection[0],
                  posts: {
                    collection: [
                      ...previousData.blogs.collection[0].posts.collection,
                      ...fetchMoreResult.blogs.collection[0].posts.collection,
                    ],
                    metadata: {
                      ...fetchMoreResult.blogs.collection[0].posts.metadata,
                    },
                    __typename:
                      previousData.blogs.collection[0].posts.__typename,
                  },
                },
              ],
              __typename: previousData.blogs.__typename,
            },
          };
        },
      });
    }
    return Promise.resolve();
  }, [metadata, fetchMore, variables]);

  return { called, posts, loading, metadata, handleNext };
};
